import type {
  Fields,
  PostType,
  MutationCreatePostTypeArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { getPostTypesKey } from '../../utils/keys/post.key.js'

export const useCreatePostType = (options?: {
  fields?: Fields.PostTypeFields
  useMutationOptions?: UseMutationOptions<
    PostType,
    Error,
    MutationCreatePostTypeArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const postTypesKey = getPostTypesKey()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<PostType, Error, MutationCreatePostTypeArgs>(
    variables =>
      client.mutation({
        name: 'createPostType',
        args: {
          fields,
          variables,
        },
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries(postTypesKey)
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      onSuccess: newPostType => {
        queryClient.setQueriesData<Network>(networkKey, oldNetwork => {
          const oldPostTypes = oldNetwork?.postTypes ?? []

          return {
            ...oldNetwork,
            postTypes: [...oldPostTypes, newPostType],
          }
        })
        queryClient.invalidateQueries(postTypesKey)
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      ...useMutationOptions,
    },
  )
}
